import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, api, type NavigationPreference } from '@/api/client'
import type { Session } from '@/auth/session'
import { useT } from '@/i18n/language'
import {
  lireModeNavigationAvecPresence, MODE_NAVIGATION_PAR_DEFAUT, sauvegarderModeNavigation, type NavigationMode,
  supprimerModeNavigation,
} from '@/lib/navigationMode'

/** Réponse d'erreur 409 `STALE_VERSION`/`IDEMPOTENCY_CONFLICT` — le corps que
 * `NavigationPreferenceController` renvoie (voir `NavigationPreferenceService.java`). */
function estConflitDeVersion(error: unknown): error is ApiError {
  if (!(error instanceof ApiError) || error.status !== 409) return false
  try {
    const corps = error.detail ? JSON.parse(error.detail) as { code?: string } : undefined
    return corps?.code === 'STALE_VERSION' || corps?.code === 'IDEMPOTENCY_CONFLICT'
  } catch { return false }
}

/**
 * Préférence serveur, avec le cache local uniquement comme repli hors connexion.
 *
 * v030-po-auth-03 — la préférence est désormais versionnée et scopée par workspace
 * (v030-po-auth-02, contrat AUTH-02A). "Le serveur gagne" a deux volets distincts,
 * qu'il ne faut pas confondre : une panne réseau (statut 0) garde la valeur locale
 * *affichée* comme "en attente" — rien n'est perdu, rien n'a été accepté par le
 * serveur non plus ; un conflit de version RÉEL (409, une autre session a écrit
 * entre-temps) ne conserve PAS la tentative locale — elle n'a jamais été acceptée
 * (négatif de la carte : "stale local ne mute pas le serveur") — et expose la
 * valeur fraîche du serveur pour confirmation explicite plutôt que de l'appliquer
 * en silence.
 *
 * Audit du 2026-10-09 (AUD-CRAWL-P-NAV-1, AUD-UX-07) — deux règles ajoutées :
 *  - sans préférence LISIBLE (GET en cours ou en erreur HTTP, stockage illisible, portée
 *    anonyme), le mode est le défaut produit `MODE_NAVIGATION_PAR_DEFAUT`, jamais `discovery`
 *    que personne n'a choisi ;
 *  - SANS PROJET, la préférence n'a pas de portée serveur (elle est persistée par projet) : la
 *    bascule est appliquée et mémorisée dans ce navigateur au lieu d'être ignorée en silence.
 *
 * D-15 (suites du même audit) : SANS ORGANISATION (compte de secours), la portée est anonyme et reste
 * fermée — aucune requête, aucune clé partagée. La bascule n'y est plus ignorée en silence : elle rend un
 * message nommé par `error`, le canal que la coquille affiche déjà (`navigationModeError`).
 */
export function useNavigationMode(session?: Session, workspaceId?: string): {
  mode: NavigationMode
  setMode: (mode: NavigationMode) => void
  error: string | undefined
  source: 'server' | 'local' | 'default'
  isLoading: boolean
  isOffline: boolean
  /** Une tentative locale n'a pas encore été confirmée par le serveur (panne réseau). */
  isPendingOffline: boolean
  /** Conflit de version signalé par le serveur : la valeur qu'il porte réellement,
   *  à confirmer explicitement avant qu'elle ne remplace l'affichage courant. */
  conflict: { mode: NavigationMode } | null
  /** Adopte la valeur fraîche du serveur après un conflit — n'a d'effet que si
   *  `conflict` est non nul. */
  confirmFreshValue: () => void
  queryError: Error | null
  mutationError: Error | null
  isMutating: boolean
} {
  // Le jeton est une credential renouvelable, pas l'identité : l'inclure ferait
  // diverger cache et localStorage à chaque refresh et pourrait laisser plusieurs
  // préférences pour le même utilisateur. Organisation + WORKSPACE + utilisateur
  // forment la clé stable du périmètre RLS serveur depuis que la préférence est
  // persistée par workspace (v030-po-auth-02) — un changement de workspace est un
  // changement de PORTÉE au même titre qu'un changement d'identité, et doit donc
  // invalider le cache exactement comme lui (négatif de la carte : "changement de
  // portée invalide le cache").
  const identity = session?.organisationId && session.userId
    ? `${session.organisationId}:${workspaceId ?? ''}:${session.userId}`
    : ''
  const identityReady = identity.length > 0 && Boolean(workspaceId)
  // Compte identifié, aucun projet ouvert : la préférence vit dans ce navigateur seulement.
  const sansProjet = identity.length > 0 && !identityReady
  // Compte identifié sans organisation : la préférence n'a aucune portée (D-15).
  const sansOrganisation = Boolean(session) && !session?.organisationId
  const { t } = useT()
  const [basculeSansOrganisation, setBasculeSansOrganisation] = useState(false)
  const preferenceQueryClient = useQueryClient()
  const [fallbackState, setFallbackState] = useState(() => ({
    identity, ...lireModeNavigationAvecPresence(identity),
  }))
  const [optimisticMode, setOptimisticMode] = useState<{ identity: string, mode: NavigationMode } | null>(null)
  const [conflict, setConflict] = useState<{ identity: string, mode: NavigationMode } | null>(null)
  const [pendingOffline, setPendingOffline] = useState<{ identity: string, mode: NavigationMode } | null>(null)
  const latestMutationRef = useRef(0)
  const identityRef = useRef(identity)
  const confirmedModeRef = useRef<NavigationMode>(fallbackState.mode)
  const serverVersionRef = useRef<number | undefined>(undefined)
  const lastPersistedServerRef = useRef<{ identity: string, mode: NavigationMode } | null>(null)
  if (identityRef.current !== identity) {
    identityRef.current = identity
    confirmedModeRef.current = MODE_NAVIGATION_PAR_DEFAUT
    serverVersionRef.current = undefined
    lastPersistedServerRef.current = null
    setConflict(null)
    setPendingOffline(null)
    setBasculeSansOrganisation(false)
    latestMutationRef.current += 1
  }
  // Read the current scope synchronously. This is intentionally identity-bound so
  // mode and source cannot describe different users during the identity switch
  // render, before effects have had a chance to run.
  const currentLocal = fallbackState.identity === identity
    ? { mode: fallbackState.mode, present: fallbackState.present }
    : lireModeNavigationAvecPresence(identity)
  const setFallback = useCallback((mode: NavigationMode): void => {
    setFallbackState((previous) => previous.identity === identity && previous.mode === mode && previous.present
      ? previous : { identity, mode, present: true })
  }, [identity])
  const preference = useQuery({
    queryKey: ['navigation-preference', identity],
    queryFn: () => api.getNavigationPreference(workspaceId),
    enabled: identityReady,
  })
  // Calculee a CHAQUE rendu depuis `preference.data`, jamais seulement depuis les refs
  // synchronisees par effet : un GET qui vient de repondre et une premiere mutation
  // lancee dans la foulee peuvent courir avant que l'effet de synchronisation n'ait
  // eu la main, sans quoi `previous`/`expectedVersion` refleteraient encore la valeur
  // PAR DEFAUT ('complete') plutot que la valeur reellement confirmee et affichee.
  const enConflitOuHorsLigne = conflict?.identity === identity || pendingOffline?.identity === identity
  const confirmedMode: NavigationMode = enConflitOuHorsLigne
    ? confirmedModeRef.current
    : (preference.data?.mode ?? confirmedModeRef.current)
  const confirmedVersion: number | undefined = enConflitOuHorsLigne
    ? serverVersionRef.current
    : (preference.data?.mode ? preference.data.version : serverVersionRef.current)
  const update = useMutation({
    mutationFn: (mode: NavigationMode) => api.setNavigationPreference(mode, {
      workspaceId, expectedVersion: confirmedVersion,
    }),
    meta: { retour: 'insitu' },
    onMutate: async (mode) => {
      const previous = confirmedMode
      const mutationId = latestMutationRef.current
      if (identityReady) {
        setFallback(mode)
        setOptimisticMode({ identity, mode })
        setConflict(null)
        setPendingOffline(null)
      }
      return { previous, present: currentLocal.present, identity, mutationId }
    },
    onError: (error, mode, context) => {
      if (!context || context.identity !== identityRef.current || context.mutationId !== latestMutationRef.current) return
      if (error instanceof ApiError && error.status === 0) {
        // Panne réseau : rien n'est perdu, rien n'est accepté — la tentative reste
        // visible, marquée en attente, jusqu'au retour du réseau.
        setPendingOffline({ identity: context.identity, mode })
        return
      }
      if (estConflitDeVersion(error)) {
        // Conflit RÉEL : le serveur a refusé, zéro mutation n'a eu lieu (négatif de
        // la carte). On ne garde pas la tentative locale comme si elle avait
        // réussi ; on revient à la dernière valeur confirmée et on rejoue le GET
        // pour connaître la valeur fraîche à proposer en confirmation. `conflict`
        // est armé ICI, synchrone avec le rejet, plutôt que dans un effet séparé
        // qui course avec le refetch déclenché juste en dessous.
        setOptimisticMode(null)
        setConflict({ identity: context.identity, mode: confirmedModeRef.current })
        preferenceQueryClient.invalidateQueries({ queryKey: ['navigation-preference', context.identity] })
        return
      }
      const previous = context?.previous ?? MODE_NAVIGATION_PAR_DEFAUT
      confirmedModeRef.current = previous
      setOptimisticMode(null)
      // Restore the exact pre-mutation presence in memory as well as on disk.
      // An absent key must not become a synthetic local fallback after rollback.
      setFallbackState({ identity: context.identity, mode: previous, present: context.present })
      if (context.present) sauvegarderModeNavigation(previous, context.identity)
      else supprimerModeNavigation(context.identity)
    },
    onSuccess: (result: NavigationPreference, _variables, context) => {
      if (!context || context.identity !== identityRef.current || context.mutationId !== latestMutationRef.current) return
      // Keep the rendered value aligned with the server response immediately;
      // otherwise the query's previous mode wins over the optimistic fallback.
      if (identityReady) {
        confirmedModeRef.current = result.mode
        serverVersionRef.current = result.version
        setOptimisticMode(null)
        setPendingOffline(null)
        preferenceQueryClient.setQueryData(['navigation-preference', context.identity], result)
        lastPersistedServerRef.current = { identity: context.identity, mode: result.mode }
        sauvegarderModeNavigation(result.mode, context.identity)
        setFallback(result.mode)
      }
    },
  })
  useEffect(() => {
    const local = lireModeNavigationAvecPresence(identity)
    confirmedModeRef.current = local.mode
    setOptimisticMode(null)
    setFallbackState({ identity, ...local })
  }, [identity])
  useEffect(() => {
    // La toute PREMIERE reponse GET pour cette identite etablit la base confirmee
    // meme si une mutation a demarre entre-temps (l'utilisateur a pu basculer le
    // mode avant que ce GET n'ait eu la main) : ce GET decrit l'etat serveur
    // ANTERIEUR a cette mutation, le persister ne contredit rien de l'edition
    // optimiste en cours (suivie separement par `optimisticMode`/`fallbackState`).
    // Une reponse GET SUBSEQUENTE (refetch), elle, doit respecter la garde
    // habituelle : ni pendant une mutation en cours, ni pendant une tentative
    // hors-ligne encore en attente, un GET arrive en retard ne doit pas effacer
    // silencieusement une edition locale qui n'a pas encore eu sa chance d'etre
    // rejouee.
    const baseDejaEtablie = lastPersistedServerRef.current?.identity === identity
    if (preference.data?.mode && (!baseDejaEtablie || (!update.isPending && !pendingOffline))) {
      if (conflict) {
        // GET rejoué après un 409 : c'est la valeur fraîche à PROPOSER, pas encore
        // à appliquer — l'utilisateur doit la confirmer explicitement.
        if (preference.data.mode !== conflict.mode) {
          setConflict({ identity, mode: preference.data.mode })
        }
        serverVersionRef.current = preference.data.version
        return
      }
      confirmedModeRef.current = preference.data.mode
      serverVersionRef.current = preference.data.version
      setFallback(preference.data.mode)
      if (lastPersistedServerRef.current?.identity !== identity || lastPersistedServerRef.current.mode !== preference.data.mode) {
        lastPersistedServerRef.current = { identity, mode: preference.data.mode }
        sauvegarderModeNavigation(preference.data.mode, identity)
      }
    }
  }, [preference.data, identity, update.isPending, pendingOffline, conflict, setFallback])
  const confirmFreshValue = useCallback((): void => {
    if (!conflict || conflict.identity !== identity) return
    confirmedModeRef.current = conflict.mode
    setFallback(conflict.mode)
    sauvegarderModeNavigation(conflict.mode, identity)
    lastPersistedServerRef.current = { identity, mode: conflict.mode }
    setConflict(null)
  }, [conflict, identity, setFallback])

  const isOffline = identityReady
    && ((preference.error instanceof ApiError && preference.error.status === 0) || pendingOffline != null)
  // Repli local LÉGITIME, et seulement lui : sans projet (la préférence ne vit que dans ce
  // navigateur), ou hors connexion avec un cache présent. Partout ailleurs — GET en cours, erreur
  // HTTP, portée anonyme — aucune préférence n'est lisible : le défaut produit.
  const repliLocal = currentLocal.present && (sansProjet
    || (identityReady && preference.error instanceof ApiError && preference.error.status === 0))

  return {
    mode: (pendingOffline?.identity === identity ? pendingOffline.mode : undefined)
      ?? (optimisticMode?.identity === identity ? optimisticMode.mode : undefined)
      ?? (conflict?.identity === identity ? confirmedModeRef.current : undefined)
      ?? preference.data?.mode
      ?? (repliLocal ? currentLocal.mode : MODE_NAVIGATION_PAR_DEFAUT),
    setMode: (mode) => {
      if (identityReady) {
        latestMutationRef.current += 1
        update.mutate(mode)
      } else if (sansProjet) {
        // P-NAV-1 : la bascule était IGNORÉE sans projet (aucun effet, aucun message). Elle est
        // appliquée et mémorisée localement ; si le stockage refuse l'écriture, la valeur tient
        // en mémoire pour la session (`setFallback`). La portée anonyme, elle, reste fermée :
        // aucune clé partagée n'est écrite.
        sauvegarderModeNavigation(mode, identity)
        setFallback(mode)
      } else if (sansOrganisation) {
        setBasculeSansOrganisation(true)
      }
    },
    error: preference.error?.message ?? update.error?.message ?? (sansOrganisation && basculeSansOrganisation
      ? t('navMode.sansOrganisation', 'Le mode de navigation se règle par organisation : ce compte n’en a pas.')
      : undefined),
    source: pendingOffline?.identity === identity ? 'local'
      : optimisticMode?.identity === identity ? 'local'
      : preference.data?.mode ? 'server'
      : repliLocal ? 'local' : 'default',
    isLoading: identityReady && preference.isPending,
    isOffline,
    isPendingOffline: pendingOffline?.identity === identity,
    conflict: conflict?.identity === identity ? { mode: conflict.mode } : null,
    confirmFreshValue,
    queryError: preference.error ?? null,
    mutationError: update.error ?? null,
    isMutating: update.isPending,
  }
}
